import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '@/i18n';
import { useVehicles } from '@/features/vehicles/useVehicles';
import { useTenantTimeZone } from '@/features/auth/useTenantTimeZone';
import { formatClock } from '@/features/trips/format';
import { useActiveSos, useAcknowledgeSos } from './useActiveSos';
import { useSosAudio } from './useSosAudio';
import { isOpenSos, type ActiveSosItem } from './types';
import { ListenControl } from '@/features/listen/ListenControl';

/**
 * 🔴 SLICE 85 — DECISION D5 (Mahad): a SOS whose PRESS is older than this RINGS no
 * alarm. It is history only — the red banner and the flashing map marker stay (so it
 * can still be acknowledged), but there is no sound and no "Enable sound" prompt. The
 * press age is measured on the SERVER clock (`serverTime − observedAt` from
 * `GET /api/alerts/active-sos`), NEVER the browser clock, so a device with a skewed
 * clock can neither silence a fresh alarm nor prolong a stale one.
 *
 * This MIRRORS the api's push freshness gate `SOS_PUSH_FRESHNESS_MS` in
 * `api/src/modules/push/wiring.ts` (also `now − observedAt < 5 min`): the two must
 * agree, or a phone and the dashboard would disagree about the same SOS — one ringing
 * while the other is silent.
 */
const SOS_RING_MAX_AGE_MS = 5 * 60_000;

/** Press age on the SERVER clock. A SOS "rings" only while this is under
 *  `SOS_RING_MAX_AGE_MS`. An unparseable `observedAt` is treated as infinitely old
 *  (no ring) rather than ringing forever off a bad timestamp. */
function sosPressAgeMs(sos: ActiveSosItem, serverNowMs: number): number {
  const observed = Date.parse(sos.observedAt);
  return Number.isNaN(observed) ? Number.POSITIVE_INFINITY : serverNowMs - observed;
}

/**
 * THE GLOBAL SOS BANNER (slice 85, PLAN §8). Mounted ONCE in the authenticated
 * `AppShell`, above every route, so a panic press is unmissable on whatever screen
 * the fleet manager is looking at. It:
 *
 *   - lists every OPEN (unacknowledged) SOS, newest first, stacked when several;
 *   - loops the alarm while some open SOS is FRESH (press under 5 min on the server
 *     clock, D5 above) via `useSosAudio`, and, when audio is not yet armed, shows a
 *     one-tap "Enable sound" control on each fresh row instead of failing silently
 *     (browser autoplay, PLAN §13). A STALE SOS still shows its banner row but makes
 *     no sound and offers no "Enable sound" prompt;
 *   - resolves the vehicle name/plate from the EXISTING vehicles query, exactly
 *     as every other alert surface does (seam §9b — clients resolve the name);
 *   - "View on map" → the live map, where the SOS vehicle's marker flashes red;
 *   - "Acknowledge" → `POST /api/alerts/:id/acknowledge`; the acked row drops out
 *     of the active list, so the banner clears itself with no extra state here.
 *
 * Renders NOTHING when there is no open SOS, so it is never a dead strip — and the
 * alarm hook still mounts (to install the first-gesture arming listener) via the
 * zero-SOS path. Red is danger/status (tokens `--raad-sos` / `--raad-onsos`),
 * never brand.
 */
export function SosBanner() {
  const { t } = useTranslation();
  const locale = i18n.language;
  const { timeZone } = useTenantTimeZone();
  const sosQuery = useActiveSos();
  const vehiclesQuery = useVehicles();
  const acknowledge = useAcknowledgeSos();

  const open = useMemo<ActiveSosItem[]>(
    () => (sosQuery.data?.sos ?? []).filter(isOpenSos),
    [sosQuery.data],
  );

  const plateById = useMemo(() => {
    const m = new Map<string, { plate: string; label: string | null; micFitted: boolean }>();
    for (const v of vehiclesQuery.data?.vehicles ?? [])
      m.set(v.id, { plate: v.plate, label: v.label, micFitted: v.micFitted });
    return m;
  }, [vehiclesQuery.data]);

  // The SERVER clock for this feed (D5). Recomputed on every refetch, so an SOS that
  // was fresh last poll ages out of ringing on a later one, even while still open.
  const serverNowMs = useMemo(() => {
    const parsed = Date.parse(sosQuery.data?.serverTime ?? '');
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }, [sosQuery.data]);

  const isFreshSos = useCallback(
    (sos: ActiveSosItem) => sosPressAgeMs(sos, serverNowMs) < SOS_RING_MAX_AGE_MS,
    [serverNowMs],
  );

  const hasOpenSos = open.length > 0;
  // The alarm rings only while SOME open SOS is fresh (D5). A banner full of only
  // stale SOS stays visible (below) but silent.
  const hasFreshOpenSos = useMemo(() => open.some((sos) => isFreshSos(sos)), [open, isFreshSos]);
  const { armed, enableSound } = useSosAudio(hasFreshOpenSos);

  if (!hasOpenSos) return null;

  return (
    <div
      // `role="alert"` + assertive: a screen reader announces it the moment it
      // appears, which is the whole point of a panic banner.
      role="alert"
      aria-live="assertive"
      data-testid="sos-banner"
      className="sticky top-0 z-50 flex flex-col gap-px bg-raad-sos/40"
    >
      {open.map((sos) => {
        const v = plateById.get(sos.vehicleId);
        const name = v?.label?.trim() || v?.plate || t('sos.unknownVehicle');
        const time = formatClock(locale, Date.parse(sos.observedAt), timeZone);
        return (
          <div
            key={sos.id}
            data-testid="sos-banner-row"
            data-alert-id={sos.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-raad-sos px-4 py-2.5 text-raad-onsos sm:px-6"
          >
            {/* The pulsing SOS glyph — a bell, pulsing, so the banner reads as
                alarming even with the sound disabled. */}
            <span className="flex items-center gap-2.5">
              <span className="relative flex size-6 flex-none items-center justify-center">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-raad-onsos/50" aria-hidden="true" />
                <svg className="relative" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.7 21a2 2 0 0 1-3.4 0" />
                </svg>
              </span>
              <span className="min-w-0">
                <span className="font-display text-sm font-extrabold uppercase tracking-wide">
                  {t('sos.banner', { vehicle: name })}
                </span>
                <span className="block text-[12px] font-medium text-raad-onsos/90">
                  {t('sos.pressedAt', { time })}
                </span>
              </span>
            </span>

            <span className="ml-auto flex items-center gap-2">
              {/* "Enable sound" appears only for a FRESH SOS (D5): a stale one must
                  never offer to start an alarm it will not play. */}
              {!armed && isFreshSos(sos) ? (
                <button
                  type="button"
                  onClick={enableSound}
                  data-testid="sos-enable-sound"
                  className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-raad-onsos/50 px-3 text-[13px] font-semibold text-raad-onsos transition-colors hover:bg-raad-onsos/15"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M11 5 6 9H2v6h4l5 4z" />
                    <path d="M15.5 8.5a5 5 0 0 1 0 7 M19 5a9 9 0 0 1 0 14" />
                  </svg>
                  {t('sos.enableSound')}
                </button>
              ) : null}
              {/* SLICE 86 — Listen, inside the SOS banner row (PLAN §4). Renders
                  only when a mic is fitted AND the user is owner/manager; the
                  control self-gates (absence over disabled) and borrows the
                  on-SOS palette to stay legible on red. Listen works even while
                  the tenant is suspended, exactly as Acknowledge does. */}
              <ListenControl
                vehicleId={sos.vehicleId}
                vehicleName={name}
                micFitted={v?.micFitted === true}
                variant="banner"
              />
              <Link
                to="/map"
                data-testid="sos-view-map"
                className="inline-flex min-h-9 items-center rounded-lg border border-raad-onsos/50 px-3 text-[13px] font-semibold text-raad-onsos transition-colors hover:bg-raad-onsos/15"
              >
                {t('sos.viewOnMap')}
              </Link>
              <button
                type="button"
                onClick={() => acknowledge.mutate(sos.id)}
                disabled={acknowledge.isPending}
                data-testid="sos-acknowledge"
                className="inline-flex min-h-9 items-center rounded-lg bg-raad-onsos px-4 text-[13px] font-bold text-raad-sos transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {t('sos.acknowledge')}
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}
